import { Check, type LucideIcon } from "lucide-react";
import { type ReactNode, useId } from "react";

import useTranslations from "@/shared/lang/useTranslations";
import { cn } from "@/shared/lib/utils";

export interface SetupStepProps {
  icon: LucideIcon;
  title: ReactNode;
  done: boolean;
  /** The last step draws no connector under its badge. */
  last?: boolean;
  children: ReactNode;
}

function StepBadge({ icon: Icon, done, className }: { icon: LucideIcon; done: boolean; className?: string }) {
  return (
    <span
      aria-hidden="true"
      className={cn(
        "flex size-8 shrink-0 items-center justify-center rounded-full border transition-colors duration-200 motion-reduce:transition-none",
        done
          ? "border-primary bg-primary text-primary-foreground"
          : "border-border bg-card text-muted-foreground",
        className,
      )}
    >
      {done ? (
        <Check className="size-4 animate-jump-in animate-duration-300 motion-reduce:animate-none" />
      ) : (
        <Icon className="size-4" />
      )}
    </span>
  );
}

/** One stage of the setup form on a vertical rail; the badge turns into a check once the stage is complete. */
export function SetupStep({ icon, title, done, last = false, children }: SetupStepProps) {
  const { t } = useTranslations("payoutBatches");
  const titleId = useId();

  return (
    <section aria-labelledby={titleId} className="sm:grid sm:grid-cols-[2rem_minmax(0,1fr)] sm:gap-x-4">
      <div className="hidden sm:flex sm:flex-col sm:items-center">
        <StepBadge icon={icon} done={done} />
        {!last && (
          <span
            aria-hidden="true"
            className={cn(
              "mt-2 w-px flex-1 transition-colors duration-200 motion-reduce:transition-none",
              done ? "bg-primary/40" : "bg-border",
            )}
          />
        )}
      </div>
      <div className={cn("min-w-0 space-y-3", !last && "pb-8")}>
        <h2 id={titleId} className="flex min-h-8 items-center gap-3 text-base font-semibold text-foreground">
          <StepBadge icon={icon} done={done} className="sm:hidden" />
          <span>
            {title}
            {done && <span className="sr-only"> · {t("setup_step_done")}</span>}
          </span>
        </h2>
        {children}
      </div>
    </section>
  );
}
